import { useEffect, useRef } from 'react';
import {
  isDocumentVisible,
  resolveBackgroundPollIntervalMs,
  subscribePageVisibility,
} from '@/utils/pageVisibility';

/**
 * 可见性感知轮询：前台按 intervalMs；后台按 backgroundIntervalMs（默认 3×，最低 12s）或 pause。
 * 切回前台立即 tick 一次。
 *
 * @param {() => (void|Promise<void>)} callback
 * @param {{
 *   enabled?: boolean,
 *   intervalMs?: number,
 *   backgroundIntervalMs?: number|'pause'|false,
 *   runImmediately?: boolean,
 * }} [options]
 */
export function useVisibilityAwareInterval(callback, options = {}) {
  const {
    enabled = true,
    intervalMs = 3000,
    backgroundIntervalMs,
    runImmediately = true,
  } = options;

  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let timerId = null;
    const visibleMs = Math.max(500, Number(intervalMs) || 3000);
    const hiddenMs = resolveBackgroundPollIntervalMs(visibleMs, backgroundIntervalMs);

    const clearTimer = () => {
      if (timerId != null) {
        window.clearTimeout(timerId);
        timerId = null;
      }
    };

    const runTick = async () => {
      if (cancelled) return;
      try {
        await callbackRef.current?.();
      } catch {
        /* 调用方自行处理；此处不中断调度 */
      }
    };

    const schedule = () => {
      clearTimer();
      if (cancelled) return;
      const hidden = !isDocumentVisible();
      if (hidden && hiddenMs == null) return;
      const delay = hidden ? hiddenMs : visibleMs;
      timerId = window.setTimeout(async () => {
        await runTick();
        if (!cancelled) schedule();
      }, delay);
    };

    const onVisibility = (visible) => {
      if (cancelled) return;
      if (visible) {
        void runTick().then(() => {
          if (!cancelled) schedule();
        });
        return;
      }
      schedule();
    };

    if (runImmediately) {
      void runTick().then(() => {
        if (!cancelled) schedule();
      });
    } else {
      schedule();
    }

    const unsubscribe = subscribePageVisibility(onVisibility);
    return () => {
      cancelled = true;
      clearTimer();
      unsubscribe();
    };
  }, [enabled, intervalMs, backgroundIntervalMs, runImmediately]);
}

export default useVisibilityAwareInterval;
